import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import { View } from 'react-native';
import moment from 'moment';
import { MysteryShopperClient, MysteryShopperProfileMemberType, SaveProfileValueDto } from '../../../../api/api.g';
import useApi from '../../../../hooks/api-hook';
import useTranslation from '../../../../hooks/translation-hook';
import CustomTextInput, { ValidationResult, InputTypeValidation } from '../../../common/CustomTextInput';
import FieldErrorMessage from '../../../common/FieldErrorMessage';
import Label from '../../../common/Label';
import ProfileModal from '../ProfileModal';
import Popup from '../../../common/Popup';
import useUserPrefsService from '../../../../hooks/userPrefsService-hook';
import CustomCheckbox from '../../../common/CustomCheckbox';
import CustomDatePicker from '../../../common/CustomDatePicker';
import CustomRadioButton from '../../../common/CustomRadioButton';
import { ProfileMemberItemProps } from '../ProfileMemberItem';
import MoreInfoButton from '../../../common/MoreInfoButton';
import useCustomTheme from '../../../../hooks/theme-hook';
import { isNumericFamilyProfile, isValidFamilyProfileValue } from '../../../../helpers/familyProfile';
import FamilyProfileFields from './FamilyProfileFields';

export interface ProfileMemberPopupProps {
  profileProps: ProfileMemberItemProps;
  height?: number;
  currentPage?: number;
  maxPage?: number;
  suspendDrawer?: boolean;
  onClose?: () => void;
  onConfirm?: (userPrefs) => void;
}

const ProfileMemberPopup = (props: ProfileMemberPopupProps) => {
  const t = useTranslation();
  const theme = useCustomTheme();
  const userPrefService = useUserPrefsService();
  const apiMysteryShopper = useApi(MysteryShopperClient);
  const [forceValidation, setForceValidation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [profileProps, setProfileProps] = useState<ProfileMemberItemProps>(props.profileProps);

  const getFamilyValidationMessage = (key: string) =>
    t(key === 'HouseholdSize' ? 'profile-family-invalid-size' : 'profile-family-invalid-ages');

  const saveProfileMutation = useMutation({
    mutationFn: (mutationProps: ProfileMemberItemProps) => {
      const profileValue = new SaveProfileValueDto();

      profileValue.init({
        key: mutationProps.profileMember.key,
        value: mutationProps.profileMember.valueType === MysteryShopperProfileMemberType.Int32 && !mutationProps.value?.trim()
          ? null
          : mutationProps.value,
      });

      return apiMysteryShopper
        .saveProfile(profileValue)
        .then(() => {
          userPrefService.updateUserProfileInfo((userPrefs) => {
            props.onClose && props.onClose();
            props.onConfirm && props.onConfirm(userPrefs);
            setIsLoading(false);
            setProfileProps(undefined);
          }, () => {
            setSaveError(t('Something-went-wrong-Please-try-again-later'));
            setIsLoading(false);
          });
        })
        .catch(() => {
          setSaveError(t('Something-went-wrong-Please-try-again-later'));
          setIsLoading(false);
        });
    },
  });

  const handleOnConfirm = () => {
    setSaveError(undefined);
    if (isNumericFamilyProfile(profileProps.profileMember.key) &&
        !isValidFamilyProfileValue(profileProps.profileMember.key, profileProps.value)) {
      setProfileProps(prev => ({ ...prev, validation: { valid: false,
        message: getFamilyValidationMessage(profileProps.profileMember.key) } }));
      return;
    }
    if (profileProps.profileMember.valueType === MysteryShopperProfileMemberType.Date) {
      if (!profileProps.value) {
        setProfileProps((prev) => {
          let newProps = { ...prev };
          newProps.validation = { valid: false, message: t('Please-fill-the-form') };
          return newProps;
        });

        return;
      }
      if (profileProps.value.trim().length < 10) {
        return;
      }
      const parsedDate = moment(profileProps.value, ['YYYY-MM-DD', 'YYYY/MM/DD'], true);
      if (!parsedDate.isValid()) {
        return;
      }
      const date = parsedDate.toDate();

      if (profileProps.profileMember.key === 'BirthDate') {
        const today = new Date();
        const age = today.getFullYear() - date.getFullYear();
        const m = today.getMonth() - date.getMonth();

        if (age < 14 || (age === 14 && m < 0) || (age === 14 && m === 0 && today.getDate() < date.getDate())) {
          setProfileProps((prev) => {
            let newProps = { ...prev };
            newProps.validation = { valid: false, message: t('age-requirement') };
            return newProps;
          });
          return;
        }
      }
    }

    if (
      profileProps.profileMember.valueType === MysteryShopperProfileMemberType.SingleChoice ||
      profileProps.profileMember.valueType === MysteryShopperProfileMemberType.MultipleChoices
    ) {
      if (!profileProps.value) {
        setProfileProps((prev) => {
          let newProps = { ...prev };
          newProps.validation = { valid: false, message: t('Please-fill-the-form') };
          return newProps;
        });
        return;
      }
    }

    setIsLoading(true);
    saveProfileMutation.mutate(profileProps);
  };

  const handleTextChange = (value: string) => {
    setProfileProps((prev) => {
      let props = { ...prev };
      props.value = value;
      props.validation = undefined;

      return props;
    });
  };

  const multiChoiceChange = (key: number, checked: boolean) => {
    setProfileProps((prev) => {
      let newProps = { ...prev };
      let values = newProps.value?.split(',') ?? [];

      const i = values?.findIndex((x) => x === key.toString()) ?? -1;

      if (i > -1) {
        values.splice(i, 1);
      }

      if (checked) {
        values.push(key.toString());
      }

      if (values && values.length > 1) {
        newProps.value = values?.join(',') ?? '';
      } else {
        newProps.value = values[0];
      }

      return newProps;
    });
  };

  const getPlaceHolder = (type: MysteryShopperProfileMemberType) => {
    switch (type) {
      case MysteryShopperProfileMemberType.Date:
        return 'YYYY-MM-DD';
      default:
        return '';
    }
  };

  const handleValidationChange = (v: ValidationResult) => {
    setForceValidation(false);
    setProfileProps((prev) => {
      let props = { ...prev };
      props.validation = { ...v };

      return props;
    });
  };

  const getDrawerContent = (props: ProfileMemberItemProps) => {
    if (isNumericFamilyProfile(props.profileMember.key)) {
      const validationMessage = getFamilyValidationMessage(props.profileMember.key);
      const visibleError = saveError || (props.validation && !props.validation.valid ? validationMessage : undefined);
      return <View>
        <FamilyProfileFields profileKey={props.profileMember.key} value={props.value} onChange={handleTextChange} />
        <View style={{ position: 'relative' }}>
          <View aria-hidden accessibilityElementsHidden importantForAccessibility="no-hide-descendants" style={{ opacity: 0 }}>
            <FieldErrorMessage message={validationMessage} />
          </View>
          {visibleError && <View style={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
            <FieldErrorMessage message={visibleError} />
          </View>}
        </View>
      </View>;
    }

    switch (props.profileMember.valueType) {
      case MysteryShopperProfileMemberType.SingleChoice:
        return (
          <View>
            {props.profileMember?.choices.map((c, i) => {
              return (
                <CustomRadioButton
                  key={c.key}
                  label={c.label}
                  value={c.key}
                  checked={c.key.toString() === props.value}
                  onChange={(checked) => {
                    if (checked) {
                      setProfileProps((prev) => {
                        let props = { ...prev };
                        props.value = c.key.toString();
                        props.validation = { valid: true, message: '' };

                        return props;
                      });
                    }
                  }}
                />
              );
            })}
            <FieldErrorMessage message={props.validation && !props.validation.valid ? props.validation.message : undefined} />
          </View>
        );
      case MysteryShopperProfileMemberType.Date:
        return (
          <View>
            {props.profileMember.key === 'BirthDate' && (
              <Label style={{ fontSize: 13, marginBottom: 8, maxWidth: 250, fontStyle: 'italic', marginTop: -8 }} numberOfLines={5}>
                {t('age-requirement')}
              </Label>
            )}
            <CustomDatePicker
              containerStyle={{ marginTop: 10 }}
              selectedDate={props.value}
              placeholder={getPlaceHolder(props.profileMember.valueType)}
              onChangeText={handleTextChange}
              maxDate={props.profileMember.key === 'BirthDate' ? new Date() : undefined}
              hasError={!!(props.validation && !props.validation.valid)}
              errorMessage={props.validation && !props.validation.valid ? props.validation.message : undefined}
              showErrorMessage
            />
          </View>
        );
      case MysteryShopperProfileMemberType.Int32:
        return (
          <View>
            <CustomTextInput
              forceValidation={forceValidation}
              style={{ marginTop: 10 }}
              inputTypeValidation={props.profileMember.key === 'LuckyNumber' ? InputTypeValidation.luckyNumber : InputTypeValidation.int}
              value={props.value as string}
              keyboardType="numeric"
              onChangeText={handleTextChange}
              onValidationChange={handleValidationChange}
              validateOnBlur
              hasError={!!(props.validation && !props.validation.valid)}
              errorMessage={props.validation && !props.validation.valid ? props.validation.message : undefined}
              showErrorMessage
            />
          </View>
        );
      case MysteryShopperProfileMemberType.String: {
        // Passing `null` to `maxLength` can be interpreted as 0 by native, making the field non-editable.
        // Use `undefined` when not provided (and guard against NaN).
        const parsedMax = props.profileMember.maxValue ? Number.parseInt(props.profileMember.maxValue, 10) : undefined;
        const parsedMin = props.profileMember.minValue ? Number.parseInt(props.profileMember.minValue, 10) : undefined;
        const maxLength = Number.isFinite(parsedMax as number) && (parsedMax as number) > 0 ? (parsedMax as number) : undefined;
        const minLength = Number.isFinite(parsedMin as number) && (parsedMin as number) > 0 ? (parsedMin as number) : undefined;
        return (
          <View>
            <CustomTextInput
              forceValidation={forceValidation}
              style={{ marginTop: 10 }}
              value={props.value as string}
              maxLength={maxLength}
              minLength={minLength}
              onChangeText={handleTextChange}
              onValidationChange={handleValidationChange}
              validateOnBlur
              hasError={!!(props.validation && !props.validation.valid)}
              errorMessage={props.validation && !props.validation.valid ? props.validation.message : undefined}
              showErrorMessage
            />
          </View>
        );
      }
      case MysteryShopperProfileMemberType.MultipleChoices:
        return (
          <View>
            {props.profileMember?.choices.map((c, i) => {
              return (
                <CustomCheckbox
                  key={c.key}
                  label={c.label}
                  value={c.key}
                  checked={props.value?.split(',').findIndex((m) => m === c.key.toString()) > -1}
                  onChange={(checked) => multiChoiceChange(c.key, checked)}
                />
              );
            })}
            <FieldErrorMessage message={props.validation && !props.validation.valid ? props.validation.message : undefined} />
          </View>
        );
      default:
        return <></>;
    }
  };

  return (
    <Popup loading={isLoading} suspendDrawer={props.suspendDrawer}>
      <>
        {profileProps != undefined && (
          <ProfileModal
            title={t('My-informations')}
            subtitle={profileProps.profileMember.question}
            titleInfoButton={
              profileProps.profileMember.key === 'BirthDate' ? (
                <MoreInfoButton
                  title={''}
                  size={18}
                  color={theme.text.darkTurquoiseTwo?.toString()}
                  openURL={'birth-date-profile-lanla-website-support-link'}
                  suspendDrawerOnOpen={props.suspendDrawer}
                >
                  {`${t('help.birth-date-profile-member')}`}
                </MoreInfoButton>
              ) : undefined
            }
            hideCloseButton={profileProps.hideCloseButton}
            fixedWidth={isNumericFamilyProfile(profileProps.profileMember.key)}
            height={props.height}
            pageNumber={props.currentPage}
            maxPageNumber={props.maxPage}
            onClose={() => {
              props.onClose && props.onClose();
            }}
            onConfirm={() => {
              handleOnConfirm();
            }}
          >
            <View>
              {getDrawerContent(profileProps)}
              {saveError && !isNumericFamilyProfile(profileProps.profileMember.key) && <FieldErrorMessage message={saveError} />}
            </View>
          </ProfileModal>
        )}
      </>
    </Popup>
  );
};

export default ProfileMemberPopup;
